import { AspectRatio, Box, Flex, HStack, Heading, Spinner, Text, VStack, colors } from "@biblioteksentralen/react";
import { ResolvedBibliobattles, useIsIframeReady } from "@libry-content/common";
import isAfter from "date-fns/isAfter";
import isSameDay from "date-fns/isSameDay";
import { useScreenIsSmall } from "../../../utils/hooks/useScreenIsSmall";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { areTimeFragmentsUpToIndexZero, useCountDown } from "../useCountDown";

type BibliobattlesVideoStreamProps = { bibliobattles: ResolvedBibliobattles };

const liveStreams = [
  "Nov 4 2024 18:00 GMT+0100",
  "Nov 5 2024 18:00 GMT+0100",
  "Nov 6 2024 18:00 GMT+0100",
  "Nov 7 2024 18:00 GMT+0100",
  "Nov 8 2024 18:00 GMT+0100",
  "Nov 10 2024 16:00 GMT+0100",
].map((date) => new Date(date));

export const BibliobattlesVideoStream = ({ bibliobattles }: BibliobattlesVideoStreamProps) => {
  const { t } = useTranslation();
  const smallScreen = useScreenIsSmall();
  const { templateContent, startDate } = bibliobattles ?? {};

  const [isReady, iframeRef] = useIsIframeReady(templateContent.liveStreamUrl);

  const nextLivestream = liveStreams.find((date) => isSameDay(date, new Date()) || isAfter(date, new Date()));

  const timeFragments = useCountDown(nextLivestream, { smallScreen });
  const isCountDownFinished = timeFragments.every(([amount]) => amount <= 0);

  if (timeFragments.length > 4) console.error("Bibliobattles livestream countdown is not constructed for >= 1 month");

  if (!templateContent.liveStreamUrl || !startDate) return null;

  const reversedFragments = timeFragments.slice(0, 4).reverse();

  return (
    <Flex alignItems="flex-start" width="100%" maxWidth="container.lg">
      <VStack width="100%" height="100%" margin="0 auto" maxWidth={{ base: "100%", md: "80%" }} alignItems="flex-start">
        <Box margin={{ base: "0 1rem", sm: "0 2rem", md: "0" }} marginBottom={{ base: "0", md: "0.5rem" }}>
          <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }} marginBottom="0.5rem">
            {t("Se streamen!")}
          </Heading>
          <Text>{t("Hver dag fra {fraTid} - {tilTid}.", { fraTid: "18:00", tilTid: "20:00" })}</Text>
          <Text>
            {t("Finalesending {dato} fra {fraTid} - {tilTid}.", {
              dato: "10. november",
              fraTid: "16:00",
              tilTid: "18:30",
            })}
          </Text>
        </Box>
        <Box position="relative" width="100%" height="100%" borderRadius={{ base: "none", md: "xl" }} overflow="hidden">
          <AspectRatio ratio={16 / 9} borderRadius="xl" overflow="hidden">
            <iframe title="TODO" allowFullScreen ref={iframeRef} />
          </AspectRatio>
          {!isReady && (
            <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
              <Spinner size="xl" />
            </Box>
          )}
          {!isCountDownFinished && (
            <VStack
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              background="rgba(0,0,0,0.9)"
              color={colors.white}
              justifyContent="center"
            >
              <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="semibold">
                {t("Direktesending starter om")}
              </Text>
              <HStack justifyContent="center" gap={{ base: "1rem", md: "2rem" }}>
                {reversedFragments.map(([amount, unit], idx) => {
                  if (areTimeFragmentsUpToIndexZero(reversedFragments, idx)) return null;

                  return (
                    <VStack key={idx} gap="0" fontWeight="600" minWidth={{ base: "2.5rem", lg: "4rem" }}>
                      <Text fontSize={{ base: "2xl", md: "4xl" }} lineHeight="1">
                        {amount}
                      </Text>
                      <Text>{unit}</Text>
                    </VStack>
                  );
                })}
              </HStack>
            </VStack>
          )}
        </Box>
      </VStack>
    </Flex>
  );
};
